import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Search, Tag, Loader2, ChevronLeft } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * MobilePriceCheck — full-page phone version of the Price Check modal.
 * Search by name / PLU / barcode. Shows product name + selling price only
 * (no cost or markup). Safe to hand the phone to a customer.
 */
export default function MobilePriceCheck() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setSelected(null);
      setSearched(false);
      return;
    }
    const t = setTimeout(async () => {
      setBusy(true);
      setSearched(true);
      try {
        const r = await axios.get(`${API}/products/search`, {
          params: { q: q.trim(), limit: 15 },
        });
        const list = Array.isArray(r.data) ? r.data : (r.data?.products || []);
        setResults(list);
        if (list.length === 1) setSelected(list[0]);
        else setSelected(null);
      } catch {
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const fmt = (v) => `R ${Number(v || 0).toFixed(2)}`;

  return (
    <div className="p-3 max-w-md mx-auto" data-testid="mobile-price-check">
      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Scan barcode, or type PLU / name"
          className="pl-9 h-12 text-base"
          data-testid="mobile-price-check-input"
          autoFocus
          inputMode="search"
        />
      </div>

      {selected && (
        <Card className="mb-3 bg-emerald-50 border-emerald-300" data-testid="mobile-price-check-selected">
          <CardContent className="p-4 text-center">
            <button
              onClick={() => setSelected(null)}
              className="text-xs text-slate-500 flex items-center gap-1 mb-2"
            >
              <ChevronLeft className="h-3 w-3" /> Back to results
            </button>
            <div className="text-xs font-medium text-emerald-800 mb-1">PRICE</div>
            <div className="text-4xl font-black text-emerald-800 mb-2" data-testid="mobile-price-check-price">
              {fmt(selected.selling_price_incl_vat)}
            </div>
            <div className="text-sm font-semibold text-slate-900">{selected.name}</div>
            <div className="text-xs text-slate-600 mt-1">
              PLU {selected.plu_code || "—"}
              {selected.location ? ` · ${selected.location}` : ""}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              In stock: <b>{selected.current_stock ?? 0}</b> {selected.unit || "units"}
            </div>
          </CardContent>
        </Card>
      )}

      {!selected && results.length > 0 && (
        <div className="space-y-2" data-testid="mobile-price-check-results">
          {results.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelected(p)}
              className="w-full text-left p-3 rounded-xl border border-slate-200 bg-white active:bg-emerald-50 flex items-center justify-between"
              data-testid={`mobile-price-check-item-${p.plu_code || p.id}`}
            >
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500">PLU {p.plu_code || "—"}</div>
              </div>
              <div className="text-emerald-700 font-bold ml-3 whitespace-nowrap">
                {fmt(p.selling_price_incl_vat)}
              </div>
            </button>
          ))}
        </div>
      )}

      {busy && (
        <div className="flex items-center justify-center py-8 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Searching...
        </div>
      )}

      {!busy && searched && !results.length && (
        <div className="text-center text-slate-500 py-10">
          <Tag className="h-10 w-10 mx-auto mb-2 text-slate-300" />
          <div className="text-sm">No product found</div>
        </div>
      )}
    </div>
  );
}
